/*
 * IMPORTS
 */
import React from "react";
import PropTypes from "prop-types";
import JoiBrowser from "joi-browser";
import NaturalCompare from "natural-compare";
import _ from "underscore";
import Chart from "react-apexcharts";
import { useLazyQuery, useQuery } from "@apollo/client";
import { HiArrowPath, HiChatBubbleBottomCenterText } from "react-icons/hi2";
import {
  Button,
  Flex,
  IconButton,
  Text,
  useBreakpointValue,
} from "@chakra-ui/react";

/*
 * PACKAGES
 */
import Card from "components/Card";
import DateRangePicker from "components/DateRangePicker";
import CustomerAccountSelector from "components/CustomerAccountSelector";
import { MemoizedSelect } from "components/MemoizedInput";
import TableSpinner from "components/TableSpinner";

/*
 * GRAPHS
 */
import CustomerStatsReadQuery from "./__query__/index.customerStats.read.query";
import MccReadQuery from "components/MccMncSelector/__query__/index.mcc.read.query";
import CustomerAccountReadQuery from "components/CustomerAccountSelector/__query__/index.customerAccount.read.query";

/*
 * STYLES
 */
import { chartStyle } from "./index.style";

/*
 * OBJECTS
 */
//Status labels and their colors
const statusItems = [
  { name: "Accepted", color: "#1f77b4" },
  { name: "Delivered", color: "#ff7f0e" },
  { name: "Expired", color: "#2ca02c" },
  { name: "Failed", color: "#d62728" },
  { name: "None", color: "#9467bd" },
  { name: "Undeliverable", color: "#8c564b" },
];

const _GetSafeNumber = (value) => {
  const number = Number(value);

  return Number.isFinite(number) && number > 0 ? number : 0;
};

const CustomerStatsGraph = ({ title }) => {
  const _isCurrentViewMobile = useBreakpointValue({
    base: true,
    sm: true,
    md: false,
    lg: false,
    xl: false,
  });

  const [customerAccountId, setCustomerAccountId] = React.useState("");
  const [mcc, setMcc] = React.useState("");
  const [startDate, setStartDate] = React.useState(
    Object.DateRangeProvider()?.startDate,
  );
  const [endDate, setEndDate] = React.useState(
    Object.DateRangeProvider()?.endDate,
  );
  const [error, setError] = React.useState("");
  const [isRefreshLoading, setIsRefreshLoading] = React.useState(false);
  const [isSubmitLoading, setIsSubmitLoading] = React.useState(false);
  // Keep initial stats request one-time even when Apollo/cache updates re-render this component.
  const _hasLoadedInitialStatsRef = React.useRef(false);

  const _HandleCustomerAccountChange = React.useCallback(({ target }) => {
    setCustomerAccountId(target.value);
    setError("");
  }, []);

  const _HandleMccChange = React.useCallback(({ target }) => {
    setMcc(target.value);
    setError("");
  }, []);

  const _mccReadVariables = React.useMemo(() => ({ take: 1000, skip: 0 }), []);
  const _customerAccountReadVariables = React.useMemo(
    () => ({
      // Keep this aligned with the selector so the initial graph represents wholesale accounts only.
      customerType: "WHOLESALE",
      // Fetch enough accounts for the dashboard default view without changing backend pagination shape.
      take: 1000,
      skip: 0,
    }),
    [],
  );

  const _QueryMccRead = useQuery(MccReadQuery, {
    variables: _mccReadVariables,
    fetchPolicy: Object.React.App.fetchPolicy,
  });
  // Read the same wholesale customer accounts used by CustomerAccountSelector for the default graph load.
  const _QueryCustomerAccountRead = useQuery(CustomerAccountReadQuery, {
    variables: _customerAccountReadVariables,
    fetchPolicy: Object.React.App.fetchPolicy,
  });

  // Lazy query lets the graph run once after account ids exist and later only when Submit is clicked.
  const [QueryCustomerStats, QueryCustomerStatsResponse] = useLazyQuery(
    CustomerStatsReadQuery,
    {
      fetchPolicy: Object.React.App.fetchPolicy,
    },
  );

  // Derive all loaded customer account ids once per account response to avoid rebuilding ids on every render.
  const _customerAccountIds = React.useMemo(
    () =>
      _.compact(
        _QueryCustomerAccountRead?.data?.CustomerAccountRead?.map?.((j) =>
          "READ_SUCCESSFUL" === j.status ? j.id : void 0,
        ),
      ),
    [_QueryCustomerAccountRead?.data?.CustomerAccountRead],
  );

  // Load default graph data once after customer accounts are available.
  React.useEffect(() => {
    // Prevent duplicate stats calls from cache updates, rerenders, or query result reference changes.
    if (_hasLoadedInitialStatsRef.current) return;
    // Wait until customer account ids are present before calling the stats report.
    if (_.isEmpty(_customerAccountIds)) return;

    // Mark before firing the request so fast rerenders cannot schedule a second call.
    _hasLoadedInitialStatsRef.current = true;
    setIsRefreshLoading(true);
    QueryCustomerStats({
      variables: {
        // Initial load uses all wholesale account ids and an empty country filter.
        customerAccountIds: _customerAccountIds,
        country: "",
        startDate,
        endDate,
      },
    })
      .catch((e) => console.error("CustomerAccountStatsReport failed:", e))
      .finally(() => setIsRefreshLoading(false));
  }, [_customerAccountIds, QueryCustomerStats, startDate, endDate]);

  // Keep chart data derived from Apollo response to avoid an extra setState render.
  const chartData = React.useMemo(() => {
    const _statusTotals = {
      Delivered: 0,
      Accepted: 0,
      Failed: 0,
      Undeliverable: 0,
      None: 0,
      Expired: 0,
    };
    if (
      !QueryCustomerStatsResponse?.data?.CustomerAccountStatsReport?._reportData
    )
      return statusItems.map((item) => ({ ...item, value: 0 }));

    const _reportData =
      QueryCustomerStatsResponse.data.CustomerAccountStatsReport._reportData ??
      [];
    _reportData.forEach((item) => {
      _statusTotals.Delivered += Number(item.deliveredsmscount) || 0;
      _statusTotals.Accepted += Number(item.acceptedsmscount) || 0;
      _statusTotals.Failed += Number(item.failedsmscount) || 0;
      _statusTotals.Undeliverable += Number(item.undeliverablesmscount) || 0;
      _statusTotals.None += Number(item.nonesmscount) || 0;
      _statusTotals.Expired += Number(item.expiredsmscount) || 0;
    });
    return statusItems.map((item) => ({
      ...item,
      value: _statusTotals[item.name],
    }));
  }, [QueryCustomerStatsResponse?.data]);

  // Keep first paint in loading state while account ids are being prepared for the automatic stats call.
  const _isChartLoading =
    QueryCustomerStatsResponse?.loading ||
    (!QueryCustomerStatsResponse?.called &&
      (_QueryCustomerAccountRead?.loading || !_.isEmpty(_customerAccountIds)));

  const _HandleDateRangeChange = React.useCallback((rangeValue) => {
    setStartDate(rangeValue?.selection?.startDate);
    setEndDate(rangeValue?.selection?.endDate);
  }, []);

  const _HandleRefresh = React.useCallback(() => {
    if (_.isEmpty(_customerAccountIds)) return;

    setError("");
    setIsRefreshLoading(true);
    QueryCustomerStats({
      variables: {
        customerAccountIds: _customerAccountIds,
        country: "",
        startDate,
        endDate,
      },
    })
      .catch((e) => console.error("CustomerAccountStatsReport failed:", e))
      .finally(() => setIsRefreshLoading(false));
  }, [_customerAccountIds, QueryCustomerStats, startDate, endDate]);

  const _HandleSubmit = React.useCallback(() => {
    const _JoiSchema = JoiBrowser.object({
      customerAccountId: JoiBrowser.string().required(),
      country: JoiBrowser.string().required(),
    }).options({ allowUnknown: true });

    const _JoiSchemaValidate = _JoiSchema.validate({
      customerAccountId,
      country: mcc,
    });

    if (_JoiSchemaValidate.error) {
      setError(_JoiSchemaValidate.error?.message);
      return;
    }

    const _id = customerAccountId?.includes("(")
      ? customerAccountId.split("(")[1].replace(")", "").trim()
      : customerAccountId;
    const _countryName = mcc?.split("(")?.[0]?.trim() || mcc;

    setIsSubmitLoading(true);
    QueryCustomerStats({
      variables: {
        // Stats API expects an array, so selected single account id is wrapped without changing submit flow.
        customerAccountIds: [_id],
        country: _countryName,
        startDate,
        endDate,
      },
    })
      .catch((e) => console.error("CustomerAccountStatsReport failed:", e))
      .finally(() => setIsSubmitLoading(false));
  }, [customerAccountId, mcc, startDate, endDate, QueryCustomerStats]);

  const _mccOptions = React.useMemo(
    () =>
      _.compact(
        _QueryMccRead?.data?.MccRead?.map?.((j) =>
          "READ_SUCCESSFUL" === j.status
            ? `${j.countryName} (${j.mcc})`
            : void 0,
        ),
      )?.sort((a, b) => NaturalCompare(a, b)),
    [_QueryMccRead?.data?.MccRead],
  );

  // Apex receives only finite positive slices to prevent NaN SVG path generation during live data updates.
  const _activeChartData = React.useMemo(
    () => chartData.filter((item) => _GetSafeNumber(item.value) > 0),
    [chartData],
  );
  const _chartSeries = React.useMemo(
    () => _activeChartData.map((item) => _GetSafeNumber(item.value)),
    [_activeChartData],
  );
  const _chartTotal = React.useMemo(
    () => _chartSeries.reduce((total, value) => total + value, 0),
    [_chartSeries],
  );
  const _chartColors = React.useMemo(
    () => _activeChartData.map((item) => item.color),
    [_activeChartData],
  );
  const _chartOptions = React.useMemo(
    () => ({
      chart: {
        height: 340,
        type: "pie",
        toolbar: { show: false },
        animations: { enabled: false },
      },
      colors: _chartColors,
      labels: _activeChartData.map((item) => item.name),
      stroke: {
        width: 1,
        colors: ["#ffffff"],
      },
      legend: {
        position: "bottom",
        fontSize: "14px",
        horizontalAlign: "center",
        itemMargin: {
          horizontal: 8,
          vertical: 4,
        },
        markers: {
          width: 12,
          height: 12,
        },
        labels: { colors: "#1A202C" },
      },
      dataLabels: {
        enabled: true,
        formatter: (value) => `${_GetSafeNumber(value).toFixed(1)}%`,
        style: {
          fontSize: "18px",
          fontWeight: "400",
          colors: ["#ffffff"],
        },
        dropShadow: { enabled: false },
      },
      plotOptions: {
        pie: {
          dataLabels: {
            minAngleToShowLabel: 8,
          },
        },
      },
    }),
    [_activeChartData, _chartColors],
  );

  const _hasChartData = React.useMemo(
    () =>
      Number.isFinite(_chartTotal) &&
      _chartTotal > 0 &&
      _chartSeries.every((value) => Number.isFinite(value)),
    [_chartSeries, _chartTotal],
  );

  return (
    <Card
      align="center"
      bg="linear-gradient(23deg, #FFDBE7 0%, #BCC0FF 99.80%)"
      borderRadius="20px"
      direction="column"
      h="100%"
      overflow="hidden"
      p={_isCurrentViewMobile ? "12px" : "0"}
      pb={_isCurrentViewMobile ? "8px" : "8px"}
      boxShadow="14px 17px 40px 4px rgba(112, 144, 176, 0.10)"
      w="100%"
    >
      <Flex justify="space-between" align="center" pt="18px" w="100%">
        <Flex
          w="100%"
          flexDirection="row"
          align="center"
          justify="space-between"
          gap={{ base: "6px", sm: "8px", md: "12px" }}
          px={{ base: "2px", md: "20px" }}
          minW={0}
        >
          <Text
            color="rgba(43,54,116,1)"
            fontWeight="600"
            fontSize={{ base: "sm", md: "md" }}
            textAlign="left"
            whiteSpace="nowrap"
            flexShrink={0}
          >
            {title}
          </Text>
          <Flex align="center" gap={{ base: "6px", md: "8px" }} minW={0}>
            <DateRangePicker
              startDate={startDate}
              endDate={endDate}
              w={{
                base: "150px",
                sm: "170px",
                md: "210px",
                lg: "235px",
                xl: "250px",
              }}
              fontSize="13px"
              setSelectDate={_HandleDateRangeChange}
            />
            <IconButton
              aria-label="Refresh customer stats"
              icon={<HiArrowPath size="17px" />}
              onClick={_HandleRefresh}
              isDisabled={_.isEmpty(_customerAccountIds) || isRefreshLoading}
              borderRadius="8px"
              minW="34px"
              h="34px"
              bg="rgba(255,255,255,0.6)"
              color="purple"
              _hover={{ bg: "purple.800", color: "white" }}
            />
          </Flex>
        </Flex>
      </Flex>

      <Flex
        mt={{ base: "12px", md: "12px" }}
        px={{ base: "0", md: "20px" }}
        gap={{ base: "12px", md: "8px", lg: "10px" }}
        flexWrap={{ base: "wrap", md: "nowrap" }}
        alignItems="center"
        w="100%"
        minW={0}
      >
        <Flex
          flex={{ base: "1 1 100%", md: "1 1 0" }}
          maxW={{ base: "100%", xl: "220px" }}
          minW={0}
          w="100%"
        >
          <CustomerAccountSelector
            w="100%"
            minH="34px"
            height="34px"
            bg="#F4F7FE"
            name="customerAccountId"
            placeholder="Select Customer Account"
            customerType="WHOLESALE"
            value={customerAccountId}
            onChange={_HandleCustomerAccountChange}
            isRequired={true}
            isInvalid={error?.includes("customerAccountId")}
          />
        </Flex>

        <Flex
          flex={{ base: "1 1 100%", md: "1 1 0" }}
          maxW={{ base: "100%", xl: "220px" }}
          minW={0}
          w="100%"
        >
          <MemoizedSelect
            w="100%"
            minH="34px"
            bg="#F4F7FE"
            disabled={_QueryMccRead?.loading}
            name="mcc"
            value={mcc}
            height="34px"
            placeholder="Select Country"
            options={_mccOptions}
            isInvalid={error?.includes("country")}
            isRequired={true}
            onChange={_HandleMccChange}
          />
        </Flex>

        <Button
          bg="rgba(255,255,255,0.6)"
          color="purple"
          onClick={_HandleSubmit}
          isLoading={isSubmitLoading}
          flex={{ base: "1 1 100%", md: "0 0 68px" }}
          w={{ base: "100%", md: "auto" }}
          minW={{ base: "100%", md: "68px" }}
          h="34px"
          size="sm"
          fontWeight="400"
          _hover={{ bg: "purple.800", color: "white" }}
          flexShrink={0}
          ml={{ base: 0, md: "auto" }}
        >
          Submit
        </Button>
      </Flex>

      {error && (
        <Text color="red.500" fontSize="sm" mt="8px">
          {error}
        </Text>
      )}

      <Flex
        mt={{ base: "16px", md: "8px" }}
        h={{
          base: "290px",
          sm: "310px",
          md: "360px",
          lg: "400px",
          xl: "400px",
        }}
        align="center"
        justify="center"
        w="100%"
        minW={0}
        minH={0}
      >
        {_isChartLoading ? (
          <TableSpinner isLoading={true} isEmpty={false} />
        ) : !_hasChartData ? (
          <Flex
            w="70%"
            h="100%"
            align="center"
            justify="center"
            flexDir="column"
            gap="12px"
            mx="auto"
            color="secondaryGray.900"
          >
            <HiChatBubbleBottomCenterText size={24} />
            <Text w="100%" fontSize="md" textAlign="center">
              Seems that you haven't sent any message today.
            </Text>
          </Flex>
        ) : (
          <Flex
            h={{ base: "270px", sm: "300px", md: "360px" }}
            maxW={{ base: "300px", sm: "340px", md: "460px" }}
            minW={0}
            w="100%"
          >
            <Chart
              style={chartStyle}
              options={_chartOptions}
              series={_chartSeries}
              type="pie"
              height="100%"
              width="100%"
              redrawOnParentResize={true}
            />
          </Flex>
        )}
      </Flex>
    </Card>
  );
};

/*
 * PROPTYPES
 */
CustomerStatsGraph.propTypes = {
  title: PropTypes.string,
};

/*
 * EXPORTS
 */
export default React.memo(CustomerStatsGraph);
